<script>
export default {
  name: "feedbackModal",
  props: {
    name: { type: String, required: true },
    campaignKey: { type: String, required: true },
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    scaleQuestion: { type: String, default: "" },
    scaleMin: { type: Number, default: 1 },
    scaleMax: { type: Number, default: 7 },
    scaleLowLabel: { type: String, default: "Strongly disagree" },
    scaleHighLabel: { type: String, default: "Strongly agree" },
    textPrompt: { type: String, default: "" },
    textPlaceholder: { type: String, default: "Anything else you'd like to share?" },
    submitLabel: { type: String, default: "Send feedback" },
  },
  data() {
    return {
      scaleValue: null,
      textValue: "",
      submitting: false,
      submitted: false,
      error: "",
    };
  },
  computed: {
    scaleValues() {
      const arr = [];
      for (let i = this.scaleMin; i <= this.scaleMax; i++) arr.push(i);
      return arr;
    },
    canSubmit() {
      return this.scaleValue != null && !this.submitting;
    },
  },
  methods: {
    onModalShow() {
      this.scaleValue = null;
      this.textValue = "";
      this.submitting = false;
      this.submitted = false;
      this.error = "";
    },
    async submit() {
      if (!this.canSubmit) return;
      this.submitting = true;
      this.error = "";
      try {
        await window.$A.FeedbackService.SubmitCampaignFeedback({
          campaignKey: this.campaignKey,
          scaleValue: this.scaleValue,
          textValue: this.textValue,
        });
        this.submitted = true;
        this.$emit("submitted", {
          campaignKey: this.campaignKey,
          scaleValue: this.scaleValue,
          textValue: this.textValue,
        });
        setTimeout(() => this.$bvModal.hide(this.name), 900);
      } catch (e) {
        this.error = "Couldn't send feedback. Please try again.";
        console.error(e);
      }
      this.submitting = false;
    },
    cancel() {
      this.$bvModal.hide(this.name);
    },
  },
};
</script>

<template>
  <b-modal
    :id="name"
    size="md"
    hide-footer
    centered
    @show="onModalShow"
  >
    <template v-slot:modal-header>
      <div class="w-100">
        <h4 class="mb-1">{{ title }}</h4>
        <p v-if="subtitle" class="mb-0 fs-13 text-gray-500">{{ subtitle }}</p>
      </div>
    </template>

    <div v-if="submitted" class="thanks-state">
      <div class="thanks-check" aria-hidden="true">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <p class="thanks-text">Thanks for sharing.</p>
    </div>

    <form v-else @submit.prevent="submit">
      <p v-if="scaleQuestion" class="scale-question">{{ scaleQuestion }}</p>

      <div class="scale-row">
        <span class="scale-low-label">{{ scaleLowLabel }}</span>
        <div class="scale-options">
          <label v-for="n in scaleValues" :key="n" class="scale-option">
            <input
              type="radio"
              :name="`${name}-scale`"
              :value="n"
              v-model.number="scaleValue"
            />
            <span class="scale-pip">{{ n }}</span>
          </label>
        </div>
        <span class="scale-high-label">{{ scaleHighLabel }}</span>
      </div>

      <label v-if="textPrompt" class="text-prompt-label" :for="`${name}-text`">
        {{ textPrompt }}
      </label>
      <textarea
        :id="`${name}-text`"
        v-model="textValue"
        class="feedback-text"
        rows="4"
        :placeholder="textPlaceholder"
      ></textarea>

      <p v-if="error" class="feedback-error" role="alert">{{ error }}</p>

      <div class="feedback-actions">
        <button type="button" class="text-btn" @click="cancel">Cancel</button>
        <button type="submit" class="primary-btn" :disabled="!canSubmit">
          {{ submitting ? "Sending…" : submitLabel }}
        </button>
      </div>
    </form>
  </b-modal>
</template>

<style scoped>
.scale-question {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 12px;
}
.scale-row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
}
.scale-low-label,
.scale-high-label {
  font-size: 12px;
  color: #6b7280;
  flex-shrink: 0;
  max-width: 110px;
  line-height: 1.3;
}
.scale-high-label { text-align: right; }
.scale-options {
  display: flex;
  flex: 1;
  justify-content: space-between;
  gap: 6px;
}
.scale-option {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  cursor: pointer;
}
.scale-option input[type="radio"] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.scale-pip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: #fff;
  border: 1.5px solid #d1d5db;
  color: #4b5563;
  font-size: 13px;
  font-weight: 600;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease, transform 80ms ease;
}
.scale-option:hover .scale-pip {
  border-color: #019256;
  color: #019256;
}
.scale-option input[type="radio"]:checked + .scale-pip {
  background: #019256;
  border-color: #019256;
  color: #fff;
  transform: scale(1.05);
}
.scale-option input[type="radio"]:focus-visible + .scale-pip {
  outline: 2px solid #019256;
  outline-offset: 2px;
}

.text-prompt-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.01em;
  margin-bottom: 6px;
}
.feedback-text {
  width: 100%;
  min-height: 96px;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  color: #1f2937;
  background: #fff;
  outline: none;
  resize: vertical;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.feedback-text:focus {
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}

.feedback-error {
  color: #b91c1c;
  font-size: 13px;
  margin: 12px 0 0;
}

.feedback-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
}
.primary-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #019256;
  color: #fff;
  border: 0;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(1, 146, 86, 0.2);
  transition: background-color 120ms ease;
}
.primary-btn:hover:not(:disabled) { background: #017a48; }
.primary-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.text-btn {
  background: transparent;
  border: 0;
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
}
.text-btn:hover { color: #111827; background: #f3f4f6; }

.thanks-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 28px 16px 12px;
}
.thanks-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 999px;
  background: rgba(1, 146, 86, 0.12);
  color: #019256;
}
.thanks-text {
  font-size: 15px;
  color: #1f2937;
  margin: 0;
  font-weight: 600;
}
</style>
